import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, Lock, Loader2, CheckCircle2, ArrowRight, AlertTriangle, XCircle, Clock, Ban, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkedAuth, setCheckedAuth] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginMode, setLoginMode] = useState('password'); // 'password' or 'magic'

  // Check if already logged in FIRST
  useEffect(() => {
    checkAuth();
  }, []);

  // Check for token in URL (magic link verification) - only after auth check
  useEffect(() => {
    if (!checkedAuth) return;
    
    const token = searchParams.get('token');
    if (token) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      verifyToken(token);
    }
  }, [searchParams, checkedAuth]);

  const checkAuth = async () => {
    // Skip auto-login if user just logged out
    const justLoggedOut = searchParams.get('logged_out') === 'true';
    if (justLoggedOut) {
      // Clean up the URL
      window.history.replaceState({}, '', '/client/login');
      setCheckedAuth(true);
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/client/me`, {
        withCredentials: true
      });
      if (response.data && response.data.customer_id) {
        toast.success(`Welcome back, ${response.data.business_name || 'Client'}!`);
        navigate('/client/dashboard');
        return;
      }
    } catch (error) {
      // Not logged in, stay on login page
      console.error('Auth check error:', error);
    }
    setCheckedAuth(true);
  };

  const verifyToken = async (token) => {
    setVerifying(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/verify-login`, 
        { token },
        { withCredentials: true }
      );
      
      if (response.data.ok) {
        toast.success(`Welcome back, ${response.data.business_name || 'Client'}!`);
        navigate('/client/dashboard');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      toast.error('Login link expired or invalid. Please request a new one.');
    } finally {
      setVerifying(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    setLoginError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/password-login`, {
        email: email.toLowerCase(),
        password: password
      }, { withCredentials: true });
      
      if (response.data.ok) {
        toast.success(`Welcome back, ${response.data.business_name || 'Client'}!`);
        navigate('/client/dashboard');
      } else {
        const errorCode = response.data.error_code;
        const errorMsg = response.data.message;
        
        setLoginError({
          code: errorCode,
          message: errorMsg,
          reason: response.data.reason
        });
        
        if (errorCode === 'PASSWORD_NOT_SET') {
          toast.info('Password not set. Sending magic link instead...');
          setLoginMode('magic');
          handleRequestMagicLink();
        } else if (errorCode === 'INVALID_PASSWORD') {
          toast.error('Incorrect password');
        } else {
          toast.error(errorMsg || 'Login failed');
        }
      }
    } catch (error) {
      console.error('Password login failed:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMagicLink = async (e) => {
    if (e) e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    setLoginError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/request-login`, {
        email: email.toLowerCase()
      });
      
      if (response.data.ok) {
        setLinkSent(true);
        
        if (response.data.email_sent) {
          toast.success('Login link sent! Check your email.');
        } else {
          if (response.data.debug_token) {
            toast.info('Using direct login...');
            setTimeout(() => {
              verifyToken(response.data.debug_token);
            }, 1500);
          } else {
            toast.success('Login link sent! Check your email.');
          }
        }
      } else {
        const errorCode = response.data.error_code;
        const errorMsg = response.data.message;
        
        setLoginError({
          code: errorCode,
          message: errorMsg,
          reason: response.data.reason
        });
        
        toast.error(errorMsg || 'Unable to send login link.');
      }
    } catch (error) {
      console.error('Magic link request failed:', error);
      toast.error('Failed to send login link.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-sky-400 mx-auto mb-4" />
          <p className="text-slate-400">Verifying your login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg">Alaska AI Services</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
            {!linkSent ? (
              <>
                <h1 className="text-2xl font-bold text-center mb-2">Client Portal</h1>
                <p className="text-slate-400 text-center mb-6">
                  Enter your credentials to access your dashboard
                </p>

                {/* Login Mode Toggle */}
                <div className="flex bg-slate-800/50 rounded-lg p-1 mb-6">
                  <button
                    type="button"
                    onClick={() => setLoginMode('password')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      loginMode === 'password' 
                        ? 'bg-sky-500 text-white' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Password
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoginMode('magic'); setPassword(''); }}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      loginMode === 'magic' 
                        ? 'bg-sky-500 text-white' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Email Link
                  </button>
                </div>

                <form onSubmit={loginMode === 'password' ? handlePasswordLogin : handleRequestMagicLink} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
                        placeholder="your@business.com"
                        className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                        data-testid="client-email-input"
                      />
                    </div>
                  </div>

                  {loginMode === 'password' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                          placeholder="Enter your password"
                          className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                          data-testid="client-password-input"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {loginError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg border ${
                        loginError.code === 'ACCOUNT_BANNED' 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : loginError.code === 'ACCOUNT_SUSPENDED'
                          ? 'bg-amber-500/10 border-amber-500/30'
                          : loginError.code === 'PENDING_APPROVAL'
                          ? 'bg-blue-500/10 border-blue-500/30'
                          : loginError.code === 'INVALID_PASSWORD'
                          ? 'bg-red-500/10 border-red-500/30'
                          : 'bg-slate-800/50 border-slate-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {loginError.code === 'ACCOUNT_BANNED' && (
                          <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        {loginError.code === 'ACCOUNT_SUSPENDED' && (
                          <XCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        )}
                        {loginError.code === 'PENDING_APPROVAL' && (
                          <Clock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        )}
                        {(loginError.code === 'ACCOUNT_NOT_FOUND' || loginError.code === 'INVALID_PASSWORD') && (
                          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className={`text-sm font-medium ${
                            loginError.code === 'ACCOUNT_BANNED' || loginError.code === 'INVALID_PASSWORD' ? 'text-red-400' :
                            loginError.code === 'ACCOUNT_SUSPENDED' ? 'text-amber-400' :
                            loginError.code === 'PENDING_APPROVAL' ? 'text-blue-400' :
                            'text-slate-300'
                          }`}>
                            {loginError.code === 'ACCOUNT_BANNED' && 'Account Terminated'}
                            {loginError.code === 'ACCOUNT_SUSPENDED' && 'Account Suspended'}
                            {loginError.code === 'PENDING_APPROVAL' && 'Pending Approval'}
                            {loginError.code === 'ACCOUNT_NOT_FOUND' && 'Account Not Found'}
                            {loginError.code === 'INVALID_PASSWORD' && 'Invalid Password'}
                            {loginError.code === 'PASSWORD_NOT_SET' && 'Password Not Set'}
                          </p>
                          <p className="text-sm text-slate-400 mt-1">{loginError.message}</p>
                          {loginError.reason && (
                            <p className="text-xs text-slate-500 mt-1">Reason: {loginError.reason}</p>
                          )}
                          {loginError.code === 'ACCOUNT_NOT_FOUND' && (
                            <a 
                              href="/signup" 
                              className="text-sm text-sky-400 hover:text-sky-300 mt-2 inline-block"
                            >
                              Request access →
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-sky-500 hover:bg-sky-600 py-6"
                    data-testid="client-login-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {loginMode === 'password' ? 'Sign In' : 'Send Login Link'}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-sm text-slate-500 text-center mt-6">
                  {loginMode === 'password' 
                    ? "Can't remember your password? Switch to Email Link" 
                    : "We'll send a secure login link to your email"}
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-bold mb-2">Check Your Email</h2>
                <p className="text-slate-400 mb-6">
                  We've sent a login link to <span className="text-white">{email}</span>
                </p>
                <p className="text-sm text-slate-500">
                  Click the link in the email to access your dashboard.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setLinkSent(false)}
                  className="mt-6 border-slate-700 text-slate-300"
                >
                  Use Different Email
                </Button>
              </div>
            )}
          </div>

          {/* Help Link */}
          <p className="text-center text-slate-500 text-sm mt-6">
            Need help? Contact{' '}
            <a href="mailto:support@alaskaaiservices.com" className="text-sky-400 hover:underline">
              support@alaskaaiservices.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
